import React, { Fragment } from 'react';

import { USStateList, USState } from 'components/Core/Payment/PaymentForm/Card/usStates';
import Chevron from 'components/Shared/Icons/Chevron';
import useComponentVisible from 'hooks/useComponentVisible';
import { useIsMobileScreen } from 'hooks/useIsMobile';
import SegmentIO from 'reporting/SegmentIO';
import { breakpoints, colors } from 'styles/cp';

export interface Props {
  state: USState | null;
  onChange: (value: USState | null) => void;
  onBlur?: () => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  shouldFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
  hasError?: boolean;
  /** For Safari autofill - name helps the browser recognize the address field */
  name?: string;
}

export const StateField: React.FC<Props> = ({
  state,
  onChange,
  onBlur: onBlurProp,
  onFocus: onFocusProp,
  shouldFocus = true,
  disabled = false,
  placeholder = 'Select one',
  hasError = false,
  name = 'state',
}) => {
  // Use controlled input like CountryField - filter is null when showing selected value
  const [filter, setFilter] = React.useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);
  const [isOpen, setIsOpen] = React.useState(false);
  // Safari/autofill workarounds
  const justSelectedRef = React.useRef(false);
  const justAutofilledRef = React.useRef(false);
  const prevInputLengthRef = React.useRef<number>(0);
  const options = USStateList;

  const isMobile = useIsMobileScreen(); // Chevron visibility only - hidden on mobile
  const inputRef = React.useRef<HTMLInputElement>(null);

  const findStateByInput = (value: string): USState | undefined =>
    options.find(
      (x) =>
        x.code.toUpperCase() === value.toUpperCase() || x.name.toLowerCase() === value.toLowerCase()
    );

  // Compute the value to show in the input
  const valueToShow = () => {
    if (filter !== null) {
      return filter;
    }
    return state?.name || '';
  };

  const optionsToShow = () => {
    if (!filter) {
      return options;
    }
    const filterLowerCase = filter.toLowerCase();
    return options.filter(
      ({ code, name }) =>
        name.toLowerCase().includes(filterLowerCase) || code.toLowerCase().includes(filterLowerCase)
    );
  };

  const onAbandonChange = () => {
    setIsOpen(false);
    const currentValue = filter || '';
    const matchedState = findStateByInput(currentValue);

    if (matchedState) {
      // Valid state - update if different
      if (!state || state.code !== matchedState.code) {
        onChange(matchedState);
      }
    } else if (currentValue) {
      // Invalid non-empty value - clear state and show error
      onChange(null);
    } else if (state) {
      // Empty value with previous state - clear state
      onChange(null);
    }

    // Reset filter to show selected value
    setFilter(null);
    onBlurProp?.();
  };

  const { ref: optionsRef, setIsComponentVisible } = useComponentVisible(false, onAbandonChange);

  const openDropdown = () => {
    setIsComponentVisible(true);
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsComponentVisible(false);
    setIsOpen(false);
    setFilter(null);
    setHighlightedIndex(-1);
  };

  const onClick = (e: React.MouseEvent) => {
    if (disabled) return;
    // Mobile Safari can fire a click after autofill - skip opening to avoid dropdown staying open
    if (justAutofilledRef.current) {
      justAutofilledRef.current = false;
      e.stopPropagation();
      return;
    }
    e.stopPropagation();
    setFilter('');
    openDropdown();
    setHighlightedIndex(0); // Highlight first option so Tab can select it
  };

  const handleOptionClick = (option: USState, isAutofill = false) => {
    justSelectedRef.current = true;
    if (isAutofill) {
      justAutofilledRef.current = true;
      SegmentIO.stateFieldAutofilled(option.code);
    } else {
      SegmentIO.stateFieldSelected(option.code);
    }
    onChange(option);
    closeDropdown();
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const prevLen = prevInputLengthRef.current;
    prevInputLengthRef.current = inputValue.length;
    setFilter(inputValue);

    // Check for complete match FIRST - handle without opening dropdown (Safari autofill
    // opens dropdown if we defer this; by checking first we never open for autofill)
    // When dropdown is open: only auto-select on 2-letter if value appeared in one shot
    // (prevLen was 0 -> autofill). If user typed "A" then "L", prevLen would be 1.
    if (inputValue.length === 2) {
      const stateByCode = findStateByInput(inputValue);
      if (stateByCode && (!isOpen || prevLen === 0)) {
        handleOptionClick(stateByCode, true);
        return;
      }
    }
    const stateByName = options.find((x) => x.name === inputValue);
    if (stateByName) {
      handleOptionClick(stateByName);
      return;
    }

    // Open dropdown when user starts typing to filter (if not already open)
    if (!isOpen && inputValue) {
      openDropdown();
    }

    // Auto-highlight the first matching option so Tab can select it
    setHighlightedIndex(inputValue ? 0 : -1);

    // If input is cleared and there was a state selected, clear the value immediately
    // This ensures form validation works even if blur doesn't fire before submit
    if (!inputValue && state) {
      onChange(null);
    }
  };

  const onBlur = () => {
    // Skip if we just made a selection (already handled)
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    const currentValue = filter ?? inputRef.current?.value ?? '';
    const matchedState = findStateByInput(currentValue);

    if (matchedState) {
      // Valid state typed - update if different from current
      if (!state || state.code !== matchedState.code) {
        onChange(matchedState);
      }
    } else if (currentValue) {
      // User typed invalid value - clear state to trigger validation error
      onChange(null);
    } else if (!currentValue && state) {
      onChange(null);
    }

    // Close dropdown when leaving field (Safari can blur without click - e.g. when filling zip)
    closeDropdown();
    // Always mark field as touched (for Formik validation to show errors)
    onBlurProp?.();
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocusProp?.(e);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const filteredOptions = optionsToShow();

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          openDropdown();
        }
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Tab':
        // On tab, select highlighted option if dropdown is open
        if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          e.preventDefault();
          handleOptionClick(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionClick(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        closeDropdown();
        break;
    }
  };

  return (
    <Fragment>
      <div className={`state-field ${disabled ? 'disabled' : ''} ${hasError ? 'has-error' : ''}`}>
        <label>
          {!isMobile && (
            <span className="chevron">
              <Chevron rotate={isOpen ? 180 : 0} />
            </span>
          )}
          <input
            ref={inputRef}
            name={name}
            value={valueToShow()}
            type="search"
            className={`input ${hasError ? 'error' : ''}`}
            placeholder={placeholder}
            onChange={onInputChange}
            onClick={onClick}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            autoComplete="address-level1"
            disabled={disabled}
          />
        </label>
        {isOpen && (
          <ul ref={optionsRef} className="options">
            {optionsToShow().map((x, i) => (
              <li
                key={x.code}
                className={`option ${i === highlightedIndex ? 'highlighted' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent input blur before we can select (blur fires before click)
                  handleOptionClick(x);
                }}
                tabIndex={i}
                ref={
                  i === highlightedIndex
                    ? (el) => el?.scrollIntoView?.({ block: 'nearest' })
                    : undefined
                }
              >
                <span className="name">{x.name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <style jsx>{`
        .state-field {
          width: 100%;
          position: relative;
          font-weight: 400;

          &.disabled {
            opacity: 0.6;
            pointer-events: none;
          }

          label {
            position: relative;
            display: flex;
            align-items: center;
          }

          label > .chevron {
            position: absolute;
            top: 50%;
            right: 12px;
            transform: translateY(-50%);
            color: silver;
            pointer-events: none;
          }

          input {
            flex: 1;
            min-width: 0;
            background: white;
            min-height: 36px;
            width: 100%;
            padding: 8px 30px 8px 12px;
            border: 1px solid #d4d7dc;
            border-radius: 4px;
            font-weight: 400;
            font-family: AvenirNextforINTUIT-Regular;
            font-size: 14px;
            cursor: pointer;

            &::placeholder {
              color: ${colors.gray05};
            }

            @media screen and (max-width: ${breakpoints.md}) {
              font-size: 14px;
              padding-right: 12px;
            }
          }

          input:hover {
            border: 1px solid ${shouldFocus ? colors.green : '#d4d7dc'};
          }

          input.error {
            border: 1px solid ${colors.error};
          }

          input.error:hover,
          input.error:focus {
            border: 1px solid ${colors.error};
          }

          input:active,
          input:focus {
            outline: none;
            border: 2px solid ${shouldFocus ? colors.green : '#d4d7dc'};
            cursor: text;
          }

          input[type='search']::-webkit-search-cancel-button {
            -webkit-appearance: none;
            opacity: 0;
            pointer-events: none;
          }

          input::-webkit-contacts-auto-fill-button {
            visibility: hidden;
            display: none !important;
            pointer-events: none;
            position: absolute;
            right: 0;
          }

          .options {
            text-align: left;
            width: 100%;
            position: absolute;
            background: white;
            padding: 8px 0;
            z-index: 10;
            max-height: 300px;
            overflow-y: auto;
            border: 1px solid #d4d7dc;
            border-radius: 4px;
            box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
            font-family: AvenirNextforINTUIT-Regular;

            @media screen and (max-width: ${breakpoints.md}) {
              max-width: 280px;
              margin: 0 auto;
              left: 0;
              right: 0;
            }

            .option {
              padding: 8px 12px;
              cursor: pointer;

              &:hover,
              &.highlighted {
                background: #e5e5e5;
              }

              .name {
                color: ${colors.gray};
              }
            }
          }
        }
      `}</style>
    </Fragment>
  );
};

export default StateField;
